import { useState, useEffect, useRef } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { api } from '../../lib/api'
import { useNavigate } from 'react-router-dom'

// Format a Date as YYYY-MM-DD in the browser's LOCAL timezone (never UTC).
// `new Date().toISOString()` returns the UTC day, which mislabels the schedule
// near midnight in US timezones.
function localYMD(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

interface ScheduleEntry {
  id: string
  schedule_date: string
  patient_name: string
  patient_id?: string
  time_slot?: string
  appointment_type?: string
  reason?: string
  position: number
}

export default function ScheduleSection() {
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [entries, setEntries] = useState<ScheduleEntry[]>([])
  const [currentDate, setCurrentDate] = useState(() => localYMD(new Date()))
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ patient_name: '', time_slot: '', appointment_type: '' })
  const fileInputRef = useRef<HTMLInputElement>(null)
  // true = replace the day's entries; false = append (for multi-page schedules)
  const importModeRef = useRef<boolean>(true)

  useEffect(() => {
    loadSchedule()
  }, [currentDate])

  async function loadSchedule() {
    setLoading(true)
    try {
      const data = await api.getSchedule(currentDate)
      setEntries(data || [])
    } catch {
      setEntries([])
    }
    setLoading(false)
  }

  function formatDate(dateStr: string) {
    // Compare against LOCAL today/tomorrow/yesterday. Using toISOString() here is
    // a bug: it returns the UTC calendar day, so a US-evening or early-morning
    // "today" gets mislabeled (e.g. this morning's schedule shown as "Yesterday").
    const base = new Date()
    const today = localYMD(base)
    const tomorrow = localYMD(new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1))
    const yesterday = localYMD(new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1))
    if (dateStr === today) return 'Today'
    if (dateStr === tomorrow) return 'Tomorrow'
    if (dateStr === yesterday) return 'Yesterday'
    const d = new Date(dateStr + 'T12:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  function changeDate(delta: number) {
    // Step the LOCAL calendar day (avoid toISOString/UTC drift across midnight).
    const d = new Date(currentDate + 'T12:00:00')
    d.setDate(d.getDate() + delta)
    setCurrentDate(localYMD(d))
  }

  const [importError, setImportError] = useState<string | null>(null)

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setImporting(true)
    setImportError(null)
    try {
      console.log('Reading file:', file.name, file.size, file.type)
      const base64 = await fileToBase64(file)
      console.log('Base64 length:', base64.length)
      
      const result = await api.extractScheduleFromScreenshot(base64)
      console.log('Extract result:', result)

      if (result.entries && result.entries.length > 0) {
        const replace = importModeRef.current
        const offset = replace ? 0 : entries.length
        const formatted = result.entries.map((entry: any, i: number) => ({
          patient_name: entry.patient_name,
          patient_id: entry.patient_id || null,
          time_slot: entry.time || entry.time_slot || null,
          appointment_type: entry.appointment_type || null,
          reason: entry.reason || null,
          position: offset + i,
        }))
        await api.bulkImportSchedule(currentDate, formatted, replace)
        // Re-load the authoritative ordered day (append returns only the new page).
        await loadSchedule()
      } else {
        setImportError('No schedule entries found in image. Try a clearer photo.')
      }
    } catch (err: any) {
      console.error('Schedule import failed:', err)
      setImportError(err.message || 'Import failed. Try again.')
    }
    setImporting(false)
    e.target.value = ''
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result.split(',')[1])
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  async function handleDelete(entryId: string) {
    try {
      await api.deleteScheduleEntry(entryId)
      setEntries(entries.filter(e => e.id !== entryId))
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  async function handleClearDay() {
    try {
      await api.clearScheduleDay(currentDate)
      setEntries([])
    } catch (err) {
      console.error('Clear day failed:', err)
    }
  }

  function startEdit(entry: ScheduleEntry) {
    setEditingId(entry.id)
    setEditForm({
      patient_name: entry.patient_name,
      time_slot: entry.time_slot || '',
      appointment_type: entry.appointment_type || '',
    })
  }

  async function saveEdit(entryId: string) {
    try {
      const updated = await api.updateScheduleEntry(entryId, editForm)
      setEntries(entries.map(e => e.id === entryId ? updated : e))
      setEditingId(null)
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  async function handleCapture(entry: ScheduleEntry) {
    // Start a CAPTURE (recording) session with this scheduled patient preloaded,
    // then jump straight into recording on the encounter page.
    try {
      const enc = await api.createEncounter({
        capture_mode: 'ambient',
        encounter_type: null,
        patient_id: entry.patient_id || undefined,
      })
      // If the entry isn't linked to a patient record yet, hand the name to the
      // encounter page via the URL. EncounterPage attaches it on load using its
      // fuzzy-match-aware flow (so a near-duplicate name prompts confirm instead of
      // being silently dropped). Previously this was a best-effort attach here that
      // swallowed the 409 fuzzy-match -> encounter opened with no patient name.
      const params = new URLSearchParams({ autoRecord: 'true' })
      if (!entry.patient_id && entry.patient_name) params.set('patient', entry.patient_name)
      navigate(`/encounter/${enc.id}?${params.toString()}`)
    } catch (err: any) {
      console.error('Failed to start capture from schedule:', err)
      const msg = String(err?.message || '')
      if (/trial|subscri|limit|cap|payment|past due|canceled/i.test(msg)) {
        alert(msg)
      } else {
        // Fall back to the new-encounter page with the patient prefilled.
        navigate(`/new-encounter?patient=${encodeURIComponent(entry.patient_name)}${entry.patient_id ? `&patientId=${entry.patient_id}` : ''}`)
      }
    }
  }

  return (
    <div className="mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between py-2 cursor-pointer select-none"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 transition-transform" style={{ transform: collapsed ? 'rotate(-90deg)' : '' }}>▼</span>
          <h2 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Schedule</h2>
        </div>

        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {/* Date nav — always visible so you can navigate to/off empty days */}
          <button onClick={() => changeDate(-1)} className="text-xs px-1 text-gray-400 hover:text-indigo-500">◀</button>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[70px] text-center">{formatDate(currentDate)}</span>
          <button onClick={() => changeDate(1)} className="text-xs px-1 text-gray-400 hover:text-indigo-500">▶</button>

          {/* Import — always available (lets you import onto an empty day) */}
          <button
            data-tour="schedule-import"
            onClick={() => { importModeRef.current = true; fileInputRef.current?.click() }}
            className="text-xs text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-400 px-2 py-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
          >
            {importing ? '⏳' : '📷 Import'}
          </button>

          {/* Add page — append a second/third screenshot to a multi-page schedule */}
          {entries.length > 0 && (
            <button
              onClick={() => { importModeRef.current = false; fileInputRef.current?.click() }}
              className="text-xs text-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-400 px-2 py-0.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
            >
              ➕ Add page
            </button>
          )}

          {/* Clear — only meaningful when the day has entries */}
          {entries.length > 0 && (
            <button
              onClick={handleClearDay}
              className="text-xs text-red-400 hover:text-red-600 px-2 py-0.5 rounded hover:bg-red-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Body */}
      {!collapsed && (
        <div className="space-y-2">
          {importError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">
              {importError}
            </div>
          )}
          {importing && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-600 text-center">
              📸 Reading schedule from image...
            </div>
          )}
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
          ) : entries.length === 0 ? (
            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2.5">
              <span className="text-xs text-gray-400 dark:text-gray-500">No appointments for {formatDate(currentDate)}</span>
            </div>
          ) : (
            entries.map(entry => (
              <div
                key={entry.id}
                className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-2.5 group"
              >
                {editingId === entry.id ? (
                  /* Edit mode */
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editForm.patient_name}
                      onChange={e => setEditForm({ ...editForm, patient_name: e.target.value })}
                      className="w-full text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded px-2 py-1 focus:border-indigo-500 focus:outline-none"
                      placeholder="Patient name"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editForm.time_slot}
                        onChange={e => setEditForm({ ...editForm, time_slot: e.target.value })}
                        className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:border-indigo-500 focus:outline-none"
                        placeholder="Time"
                      />
                      <input
                        type="text"
                        value={editForm.appointment_type}
                        onChange={e => setEditForm({ ...editForm, appointment_type: e.target.value })}
                        className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:border-indigo-500 focus:outline-none"
                        placeholder="Type"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(entry.id)}
                        className="text-xs bg-indigo-500 text-white px-3 py-1 rounded hover:bg-indigo-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-xs text-gray-500 px-3 py-1 rounded hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{entry.patient_name}</span>
                        {entry.patient_id && <span className="text-xs text-emerald-500">✓</span>}
                      </div>
                      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); startEdit(entry) }}
                          className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }}
                          className="p-1.5 text-gray-400 hover:text-red-500"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                      {entry.time_slot && <span>{entry.time_slot}</span>}
                      {entry.time_slot && entry.appointment_type && <span>•</span>}
                      {entry.appointment_type && <span>{entry.appointment_type}</span>}
                    </div>
                    <button
                      onClick={() => handleCapture(entry)}
                      className="mt-2 text-xs bg-indigo-500 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-600 transition-colors"
                    >
                      🎙 Capture
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
